import React, { useEffect, useRef, useState } from 'react';
import MainLayout from '../components/MainLayout';
import api from '../services/api';
import { Spinner } from 'react-bootstrap';
import { Check } from 'lucide-react';
import {
  niftyBuyingAPI, niftyBuyingDefaultsAPI,
  BuyingDirection, BuyingMarket, NiftyBuyingConfig, NiftyBuyingDefault, BuyingResult,
} from '../services/niftyBuyingApi';
import { brokerAPI, BrokerStatus, BrokerFeatureToggle } from '../services/brokerApi';

// "Nifty Buying" — single-leg manual NIFTY CE (Bullish) / PE (Bearish) buy, no hedge. Combines
// Manual Spread's Default/Clear/auto-save UX + NP-points SL/TSL model with the original NSE
// auto-entry's capital%+premium-range strike selection, plus two new feature-local gate filters
// (NL/NH off NIFTY-I futures, N%L/BNL/ITL off NIFTY-I/BANKNIFTY-I futures + NIFTY IT spot).

// Auto-predicted Expiry Date — client's explicit ask: don't leave the field blank requiring a
// manual pick every time (that's exactly why the ATM Delta widget looked "not working" — no
// expiry meant no data was ever fetched). Mirrors backend/src/utils/expiryUtils.ts's
// getNextTuesday/getNextThursday exactly — NIFTY weeklies expire Tuesday, SENSEX Thursday.
function predictExpiryDate(market: BuyingMarket): string {
  const today = new Date();
  const dayOfWeek = today.getDay(); // 0=Sun..6=Sat
  const targetDay = market === 'SENSEX' ? 4 : 2; // Thursday : Tuesday
  let daysToAdd: number;
  if (dayOfWeek === targetDay) {
    daysToAdd = 0;
  } else if (dayOfWeek < targetDay) {
    daysToAdd = targetDay - dayOfWeek;
  } else {
    daysToAdd = 7 - (dayOfWeek - targetDay);
  }
  const target = new Date(today);
  target.setDate(today.getDate() + daysToAdd);
  const year = target.getFullYear();
  const month = String(target.getMonth() + 1).padStart(2, '0');
  const day = String(target.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// Confirmed live as a REAL bug (client report: strike orders silently failing with "No strikes
// found" even for a clearly-populated range): a saved last-used Expiry Date was reused as-is
// forever, with no check for whether it had already passed. Once the week rolled over, the
// SENSEX panel's Expiry Date field stayed stuck on the now-expired Thursday — and the hidden
// SENSEX ATM Delta widget (kept mounted purely to feed the live-spot box, see AtmDeltaBox below)
// kept polling Dhan's rate-limited option-chain/quote endpoints for that dead, already-expired
// contract every 5 seconds, continuously, since Dhan can never return fresh quotes for it. That
// constant wasted polling was eating into the SAME shared, rate-limited Dhan quote budget the
// NIFTY panel's own strike-LTP scan depends on — confirmed live via pm2 logs showing nonstop 805
// "Too many requests" on security IDs resolving to "SENSEX 29 OCT 74700 CE/PE" (an already-past
// expiry) at the exact moment a same-page NIFTY Buy click's strike scan also came back
// all-zero/rate-limited. A saved expiry that's already in the past is now treated the same as "no
// saved expiry" — falls back to the freshly-predicted upcoming one instead.
function isPastDate(dateStr: string): boolean {
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  return dateStr < todayStr;
}

interface PanelState {
  capitalPercentage: number | '';
  rangeMin: number | '';
  rangeMax: number | '';
  slippagePercentage: number | '';
  delaySeconds: number | '';
  slPoints: number | '';
  tslNpPoints: number | '';
  targetPoints: number | '';
  slXYOffset: number | '';
  tslXYPoints: number | '';
  broker: string;
}

const defaultPanelState = (): PanelState => ({
  capitalPercentage: 5, rangeMin: 10, rangeMax: 50,
  slippagePercentage: 3, delaySeconds: 3,
  slPoints: '', tslNpPoints: '', targetPoints: '', slXYOffset: '', tslXYPoints: '', broker: 'DHAN',
});

// mysql2 returns DECIMAL columns as strings (e.g. "5.00"), which a number input then displays
// verbatim ("5.00" instead of "5") — Number(...) normalizes it to a plain JS number first.
const num = (v: number | string | null | undefined): number | '' => (v === null || v === undefined || v === '' ? '' : Number(v));

const applyDefault = (base: PanelState, d: NiftyBuyingDefault | undefined): PanelState => {
  if (!d) return base;
  return {
    ...base,
    capitalPercentage: num(d.capital_percentage) === '' ? base.capitalPercentage : num(d.capital_percentage),
    rangeMin: num(d.range_min) === '' ? base.rangeMin : num(d.range_min),
    rangeMax: num(d.range_max) === '' ? base.rangeMax : num(d.range_max),
    slippagePercentage: num(d.slippage_percentage) === '' ? base.slippagePercentage : num(d.slippage_percentage),
    delaySeconds: num(d.delay_seconds) === '' ? base.delaySeconds : num(d.delay_seconds),
    slPoints: num(d.sl_points),
    tslNpPoints: num(d.tsl_np_points),
    targetPoints: num(d.target_points),
    slXYOffset: num(d.sl_xy_offset),
    tslXYPoints: num(d.tsl_xy_points),
    broker: d.broker || base.broker,
  };
};

const DIRECTIONS: { direction: BuyingDirection; label: string; optionType: 'CE' | 'PE'; accent: string; bg: string }[] = [
  { direction: 'BULLISH', label: 'BULLISH', optionType: 'CE', accent: '#065f46', bg: '#ecfdf5' },
  { direction: 'BEARISH', label: 'BEARISH', optionType: 'PE', accent: '#991b1b', bg: '#fef2f2' },
];

const labelStyle: React.CSSProperties = { display: 'block', fontSize: '9px', fontWeight: 500, color: '#374151', marginBottom: '3px' };
const inputStyle: React.CSSProperties = { width: '100%', padding: '5px 6px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '10px' };

// Dedicated TIMER + ROI boxes, shown only once a position is open — matches the client's
// hand-drawn sketch (two boxes side by side) rather than inline text.
const TimerBox: React.FC<{ startedAt: string }> = ({ startedAt }) => {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const startMs = new Date(startedAt).getTime();
    const tick = () => setElapsed(Math.max(0, Math.floor((Date.now() - startMs) / 1000)));
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [startedAt]);
  const mm = String(Math.floor(elapsed / 60)).padStart(2, '0');
  const ss = String(elapsed % 60).padStart(2, '0');
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: '8px', fontWeight: 600, color: '#6b7280', marginBottom: '2px' }}>TIMER</div>
      <div style={{ padding: '4px 10px', fontSize: '11px', fontWeight: 700, borderRadius: '4px', border: '1px solid #d1d5db', background: '#f8f9fa', color: '#212529', minWidth: '64px' }}>
        {mm}:{ss}
      </div>
    </div>
  );
};

// Generic small labeled box, same visual shape as TimerBox/RoiBox — fills the empty space next
// to them on the open-position row with a few more useful at-a-glance details per client request.
const InfoBox: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div style={{ textAlign: 'center' }}>
    <div style={{ fontSize: '8px', fontWeight: 600, color: '#6b7280', marginBottom: '2px' }}>{label}</div>
    <div style={{ padding: '4px 10px', fontSize: '11px', fontWeight: 700, borderRadius: '4px', border: '1px solid #d1d5db', background: '#f8f9fa', color: '#212529', minWidth: '64px' }}>
      {value}
    </div>
  </div>
);

const RoiBox: React.FC<{ roiPercent: number | null | undefined }> = ({ roiPercent }) => (
  <div style={{ textAlign: 'center' }}>
    <div style={{ fontSize: '8px', fontWeight: 600, color: '#6b7280', marginBottom: '2px' }}>ROI</div>
    <div style={{
      padding: '4px 10px', fontSize: '11px', fontWeight: 700, borderRadius: '4px', minWidth: '64px',
      border: '1px solid #d1d5db',
      background: roiPercent == null ? '#f8f9fa' : roiPercent >= 0 ? '#e8f5e9' : '#ffebee',
      color: roiPercent == null ? '#6c757d' : roiPercent >= 0 ? '#1b5e20' : '#7f0000',
    }}>
      {roiPercent != null ? `${roiPercent >= 0 ? '+' : ''}${roiPercent.toFixed(2)}%` : '--'}
    </div>
  </div>
);

// Top-row BUY/SELL boxes — same widget TradingAppBar already uses for its own Buy/Sell LTP
// Diff (client's explicit ask: reuse that, not build new). spotDiff is already pre-signed
// "favorable move" (BULLISH: current−entry, BEARISH: entry−current — see niftyBuyingController's
// getOpenPositions), so unlike TradingAppBar's inverted Sell framing, both boxes here use the
// same plain rule: green when favorable, red when not.
// Client's hand-drawn spec: "Buy" (label) + CALL's Combined ROI stacked on the far left of the
// Delta widget, "Sell" (label) + PUT's Combined ROI stacked on the far right — replaces the old
// BUY/SELL spotDiff boxes entirely (not just adding ROI alongside).
// 3 stacked fields per client's explicit ask: (1) the Buy/Sell label itself, (2) the hand-drawn
// spot-points-favorable-move formula (points, not %) in its own field, (3) the original %-premium
// Combined ROI — unaffected/unchanged — in its own separate field.
// Client's explicit hand-drawn ask: a TIMER row added between the points box and the ROI box —
// ticks the elapsed time since the MOST RECENTLY opened position on this side (client's explicit
// confirmation: "last", not the oldest), same 72px-box visual language as the rest of this widget
// rather than reusing TimerBox's larger per-position styling.
const MiniTimerBox: React.FC<{ startedAt: string }> = ({ startedAt }) => {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const startMs = new Date(startedAt).getTime();
    const tick = () => setElapsed(Math.max(0, Math.floor((Date.now() - startMs) / 1000)));
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [startedAt]);
  const mm = String(Math.floor(elapsed / 60)).padStart(2, '0');
  const ss = String(elapsed % 60).padStart(2, '0');
  return (
    <div style={{
      padding: '4px 6px', fontSize: '9px', fontWeight: 700, borderRadius: '4px', textAlign: 'center', flex: 1,
      border: '1px solid #d1d5db', background: '#f8f9fa', color: '#374151',
    }}>
      {mm}:{ss}
    </div>
  );
};

// Client's explicit ask: the SENSEX Buy/Sell box's points field shows BOTH NIFTY's and SENSEX's
// own point value together (NIFTY's box stays single-value, unchanged) — two stacked mini-lines
// inside the same-width field rather than a second box, to keep the 3-row (B/T/R) shape intact.
const RoiLabelBox: React.FC<{
  label: string; points: number | null | undefined; roi: number | null | undefined; lastEntryAt?: string | null;
  secondaryPoints?: number | null; secondaryLabel?: string;
}> = ({ label, points, roi, lastEntryAt, secondaryPoints, secondaryLabel }) => {
  const pointsActive = points !== null && points !== undefined;
  const roiActive = roi !== null && roi !== undefined;
  const secondaryActive = secondaryPoints !== null && secondaryPoints !== undefined;
  const fieldStyle = (active: boolean, positive: boolean | undefined) => ({
    padding: '4px 6px', fontSize: '9px', fontWeight: 700, borderRadius: '4px', textAlign: 'center' as const, flex: 1,
    border: '1px solid #d1d5db',
    background: !active ? '#f8f9fa' : positive ? '#e8f5e9' : '#ffebee',
    color: !active ? '#6c757d' : positive ? '#1b5e20' : '#7f0000',
  });
  // Client's explicit ask: no separate "Buy"/"Sell" heading box anymore, and each row's letter
  // (B/S/T/R) sits OUTSIDE its box, to the left — not as text inside the box. label's first
  // letter is used for the points row (B for Buy, S for Sell).
  const sidePrefix = label.charAt(0).toUpperCase();
  const rowStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: '3px', width: '72px' };
  const letterStyle: React.CSSProperties = { fontSize: '8px', fontWeight: 700, color: '#374151', width: '8px', flexShrink: 0 };
  return (
    <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: '2px' }}>
      <div style={rowStyle}>
        <span style={letterStyle}>{sidePrefix}</span>
        {secondaryLabel !== undefined ? (
          <div style={{
            padding: '3px 6px', borderRadius: '4px', flex: 1, border: '1px solid #d1d5db',
            background: '#f8f9fa', display: 'flex', flexDirection: 'column', gap: '1px',
          }}>
            <span style={{ fontSize: '8px', fontWeight: 700, color: secondaryActive ? (secondaryPoints! >= 0 ? '#1b5e20' : '#7f0000') : '#6c757d' }}>
              {secondaryLabel} {secondaryActive ? Math.round(Math.abs(secondaryPoints!)) : '--'}
            </span>
            <span style={{ fontSize: '8px', fontWeight: 700, color: pointsActive ? (points! >= 0 ? '#1b5e20' : '#7f0000') : '#6c757d' }}>
              {sidePrefix} {pointsActive ? Math.round(Math.abs(points!)) : '--'}
            </span>
          </div>
        ) : (
          <div style={fieldStyle(pointsActive, pointsActive ? points! >= 0 : undefined)}>
            {pointsActive ? `${Math.round(Math.abs(points!))}` : '--'}
          </div>
        )}
      </div>
      <div style={rowStyle}>
        <span style={letterStyle}>T</span>
        {lastEntryAt ? <MiniTimerBox startedAt={lastEntryAt} /> : (
          <div style={{
            padding: '4px 6px', fontSize: '9px', fontWeight: 700, borderRadius: '4px', textAlign: 'center', flex: 1,
            border: '1px solid #d1d5db', background: '#f8f9fa', color: '#6c757d',
          }}>
            --:--
          </div>
        )}
      </div>
      <div style={rowStyle}>
        <span style={letterStyle}>R</span>
        {/* Client's explicit ask: this ROI box should "glow" like IVH/NL-NH-N-INTRA already do —
            same pulsing box-shadow keyframe mechanism (CSS var + animation), green while ROI is
            positive, red while negative, no glow while inactive ('--'). */}
        <div style={{
          ...fieldStyle(roiActive, roiActive ? roi! >= 0 : undefined),
          ...(roiActive ? { ['--rlb-glow' as any]: roi! >= 0 ? '#00e676' : '#ff1744', animation: 'rlbGlowPulse 1.1s ease-in-out infinite' } : {}),
        }}>
          {roiActive ? `${roi! >= 0 ? '+' : ''}${roi!.toFixed(2)}%` : '--'}
        </div>
        <style>{`@keyframes rlbGlowPulse { 0%, 100% { box-shadow: 0 0 2px 1px var(--rlb-glow); } 50% { box-shadow: 0 0 10px 4px var(--rlb-glow); } }`}</style>
      </div>
    </div>
  );
};

// Fills the gap left in each MarketSection's Buy/Sell row after AtmDeltaBox moved up to the
// shared top box — client's explicit ask: "Live NIFTY/SENSEX spot price (oru single box)".
const LiveSpotBox: React.FC<{ market: BuyingMarket; spot: number | null }> = ({ spot }) => {
  return (
    <div style={{ textAlign: 'center' }}>
      {/* Client's explicit ask: no separate "NIFTY"/"SENSEX" label here — the single "N"/"S"
          label next to the Expiry Date input now covers both this box and that input. */}
      <div style={{
        padding: '4px 6px', fontSize: '9px', fontWeight: 700, borderRadius: '4px', textAlign: 'center', width: '72px',
        border: '1px solid #d1d5db', background: '#f8f9fa', color: '#1f2937',
      }}>
        {spot !== null ? spot.toFixed(2) : '--'}
      </div>
    </div>
  );
};

// IVH — India VIX %L: ((VIX LTP - VIX Low) / (VIX High - VIX Low)) × 100. 2-box widget per the
// client's explicit correction (not the L/C/H 3-box): box1 is the IVH value itself, box2 is VIX's
// %-change vs its own previous close, green when VIX is up from prev close, red when down.
const IvhBox: React.FC = () => {
  const [ivh, setIvh] = useState<number | null>(null);
  const [chgPct, setChgPct] = useState<number | null>(null);
  // Client's explicit ask: the blinking glow should only show during the configured Trading
  // Hours window (Settings → Trading Hours), not all day — fetched once (that window rarely
  // changes mid-session) and rechecked against the current IST clock every 30s.
  const [withinTradingHours, setWithinTradingHours] = useState(false);

  useEffect(() => {
    const poll = () => {
      api.get('/live-data/counts').then(res => {
        const d = res.data;
        setIvh(d.ivh !== undefined && d.ivh !== null ? Number(d.ivh) : null);
        setChgPct(d.ivh_chg_pct !== undefined && d.ivh_chg_pct !== null ? Number(d.ivh_chg_pct) : null);
      }).catch(() => {});
    };
    poll();
    const interval = setInterval(poll, 3000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    let startTime = '09:15:00';
    let endTime = '15:30:00';
    let cancelled = false;
    let interval: ReturnType<typeof setInterval> | undefined;
    api.get('/trading-hours').then(res => {
      startTime = res.data.startTime || startTime;
      endTime = res.data.endTime || endTime;
    }).catch(() => {}).finally(() => {
      if (cancelled) return;
      const check = () => {
        const nowIST = new Date().toLocaleTimeString('en-GB', { timeZone: 'Asia/Kolkata', hour12: false });
        setWithinTradingHours(nowIST >= startTime.slice(0, 8) && nowIST <= endTime.slice(0, 8));
      };
      check();
      interval = setInterval(check, 30000);
    });
    return () => { cancelled = true; if (interval) clearInterval(interval); };
  }, []);

  const positive = (chgPct ?? 0) >= 0;
  const boxStyle: React.CSSProperties = {
    width: '50%', padding: '4px 2px', fontSize: '9px', fontWeight: 700, textAlign: 'center', borderRadius: '4px',
    border: `1px solid ${positive ? '#1b5e20' : '#7f0000'}`,
    background: positive ? '#c8e6c9' : '#ffcdd2', color: positive ? '#1b5e20' : '#7f0000',
  };

  // Whole-container blinking glow — client's final explicit spec (back to 4 cases, Prev Close
  // sign included, after a brief simplified-to-2-case detour that turned out to be wrong):
  //   %L >= 50% & Prev Close +VE -> RED     %L >= 50% & Prev Close -VE -> GOLD
  //   %L < 50%  & Prev Close +VE -> GOLD    %L < 50%  & Prev Close -VE -> GREEN
  let glowColor: string | null = null;
  if (withinTradingHours && ivh !== null && chgPct !== null) {
    if (ivh < 50) glowColor = chgPct >= 0 ? '#ffd700' : '#00e676';
    else glowColor = chgPct >= 0 ? '#ff1744' : '#ffd700';
  }

  return (
    <div
      style={{
        width: '150px', padding: '6px', borderRadius: '8px',
        ...(glowColor ? { ['--ivh-glow' as any]: glowColor, animation: 'ivhGlowPulse 1.1s ease-in-out infinite' } : {}),
      }}
    >
      <style>{`@keyframes ivhGlowPulse { 0%, 100% { box-shadow: 0 0 3px 1px var(--ivh-glow); } 50% { box-shadow: 0 0 16px 7px var(--ivh-glow); } }`}</style>
      <div style={{ display: 'flex', gap: '2px', marginBottom: '2px' }}>
        <small style={{ width: '50%', textAlign: 'center', fontSize: '7px', color: '#6c757d' }}>%L</small>
        <small style={{ width: '50%', textAlign: 'center', fontSize: '7px', color: '#6c757d' }}>Prev Close</small>
      </div>
      <div style={{ display: 'flex', gap: '2px' }}>
        <input type="text" readOnly value={ivh !== null ? ivh.toFixed(2) : '--'} style={boxStyle} />
        <input type="text" readOnly value={chgPct !== null ? `${positive ? '+' : ''}${chgPct.toFixed(2)}%` : '--'} style={boxStyle} />
      </div>
    </div>
  );
};

// NL / NH / N-Intra — vs NIFTY-I futures. Plain display, same nh/nl fields already used by the
// feature-local gate check backend, and the same N-Intra = NH + NL sum LivePage uses.
// NL / NH / N-Intra — FUT value (NIFTY-I futures) stacked above SPOT value (NIFTY 50 spot), same
// FUT+SPOT paired convention as N%L/BNL/ITL below (client's explicit ask to match that pattern).
const NlNhGroup: React.FC = () => {
  const [live, setLive] = useState<{ nl: number; nh: number; nlSpot: number; nhSpot: number } | null>(null);

  useEffect(() => {
    const poll = () => {
      api.get('/live-data/counts').then(res => {
        const d = res.data;
        setLive({
          nl: Number(d.nl) || 0, nh: Number(d.nh) || 0,
          nlSpot: Number(d.nl_spot) || 0, nhSpot: Number(d.nh_spot) || 0,
        });
      }).catch(() => {});
    };
    poll();
    const interval = setInterval(poll, 3000);
    return () => clearInterval(interval);
  }, []);

  // Client's explicit sketch: FUT and SPOT as column headers (once, at top), NL/NH/N-INTRA as row
  // labels down the side — not stacked per-column like the earlier layout.
  const rows: { label: string; fut: number | null; spot: number | null }[] = [
    { label: 'NL', fut: live?.nl ?? null, spot: live?.nlSpot ?? null },
    { label: 'NH', fut: live?.nh ?? null, spot: live?.nhSpot ?? null },
    { label: 'N-INTRA', fut: live ? live.nl + live.nh : null, spot: live ? live.nlSpot + live.nhSpot : null },
  ];
  const cell = (value: number | null) => (
    <div style={{
      padding: '4px 6px', fontSize: '9px', fontWeight: 600, borderRadius: '4px', textAlign: 'center',
      border: '1px solid #d1d5db', background: '#f8f9fa', color: '#212529',
    }}>
      {value !== null ? Math.round(value) : '--'}
    </div>
  );
  // Client's explicit ask: back to NL/NH/N-INTRA as COLUMN headers (across the top), FUT/SPOT as
  // row labels down the side — the transpose of the brief sketch-driven layout from before.
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '40px 50px 50px 50px', gap: '4px', alignItems: 'center' }}>
      <div />
      {rows.map(r => (
        <div key={r.label} style={{ fontSize: '8px', fontWeight: 700, color: '#374151', textAlign: 'center' }}>{r.label}</div>
      ))}
      <div style={{ fontSize: '7px', color: '#6c757d' }}>FUT</div>
      {rows.map(r => <React.Fragment key={`${r.label}-fut`}>{cell(r.fut)}</React.Fragment>)}
      <div style={{ fontSize: '7px', color: '#6c757d' }}>SPOT</div>
      {rows.map(r => <React.Fragment key={`${r.label}-spot`}>{cell(r.spot)}</React.Fragment>)}
    </div>
  );
};

// N%L / BNL — NIFTY-I / BANKNIFTY-I futures value only (client's explicit ask: revert the
// FUT+SPOT pairing, back to a single box). ITL stays SPOT (NIFTY IT has no futures contract).
const NxlBnlItlGroup: React.FC = () => {
  const [live, setLive] = useState<{ nPercL: number; bn: number; it: number } | null>(null);

  useEffect(() => {
    const poll = () => {
      api.get('/live-data/counts').then(res => {
        const d = res.data;
        setLive({ nPercL: Number(d.n_perc_l) || 0, bn: Number(d.bn) || 0, it: Number(d.it) || 0 });
      }).catch(() => {});
    };
    poll();
    const interval = setInterval(poll, 3000);
    return () => clearInterval(interval);
  }, []);

  // Client's explicit ask: each box green when its own value is >=50, red when <50; and the WHOLE
  // container additionally glows green when all three are >=50, or red when all three are <50 —
  // same glow mechanism (CSS var + pulsing box-shadow keyframe) as IvhBox above. Mixed (not all
  // three agreeing) gets per-box color only, no container glow.
  const simpleBox = (label: string, value: number | null) => {
    const active = value !== null;
    const isHigh = active && value! >= 50;
    return (
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '8px', fontWeight: 700, color: '#374151', marginBottom: '3px' }}>{label}</div>
        <div style={{
          padding: '4px 8px', fontSize: '9px', fontWeight: 600, borderRadius: '4px', textAlign: 'center',
          border: '1px solid #d1d5db', background: !active ? '#f8f9fa' : isHigh ? '#e8f5e9' : '#ffebee',
          color: !active ? '#212529' : isHigh ? '#1b5e20' : '#7f0000',
        }}>
          {value !== null ? Math.round(value) : '--'}
        </div>
      </div>
    );
  };

  const values = live ? [live.nPercL, live.bn, live.it] : null;
  const allHigh = values !== null && values.every(v => v >= 50);
  const allLow = values !== null && values.every(v => v < 50);
  const glowColor = allHigh ? '#00e676' : allLow ? '#ff1744' : null;

  return (
    <div style={{
      display: 'flex', gap: '12px', padding: '4px', borderRadius: '8px',
      ...(glowColor ? { ['--nbi-glow' as any]: glowColor, animation: 'nbiGlowPulse 1.1s ease-in-out infinite' } : {}),
    }}>
      <style>{`@keyframes nbiGlowPulse { 0%, 100% { box-shadow: 0 0 3px 1px var(--nbi-glow); } 50% { box-shadow: 0 0 16px 7px var(--nbi-glow); } }`}</style>
      {simpleBox('N%L', live?.nPercL ?? null)}
      {simpleBox('BNL', live?.bn ?? null)}
      {simpleBox('ITL', live?.it ?? null)}
    </div>
  );
};

// Delta 0.9 widget — NOT a plain ATM lookup. Client's confirmed rule: scan strikes from ATM
// outward (CE: step down toward ITM, PE: step up toward ITM) and use the FIRST strike whose
// delta magnitude reaches 0.9+; CE and PE can therefore land on two different strikes, shown
// under each column. Delta comes from Dhan's separate, rate-limited Option Chain Greeks API
// (polled every 5s, under the ~3s/request limit reported for it) and fails open to "--" if that
// call errors; %-from-Low/High use the already-proven quote fetch for whichever strike was
// selected, so those two never depend on the new, less-verified call.
const AtmDeltaBox: React.FC<{ expiryDate: string; market: BuyingMarket; onSpotUpdate?: (spot: number) => void }> = ({ expiryDate, market, onSpotUpdate }) => {
  const [data, setData] = useState<{ ce: { strike: number; delta: number | null; ltp: number | null; pctFromLow: number | null; pctFromHigh: number | null }; pe: { strike: number; delta: number | null; ltp: number | null; pctFromLow: number | null; pctFromHigh: number | null }; atmStrike: number } | null>(null);

  useEffect(() => {
    if (!expiryDate) return;
    const poll = () => {
      niftyBuyingAPI.getAtmDelta(expiryDate, market).then(res => {
        const fresh = res.data;
        // The underlying Dhan quote call (shared, rate-limited across the whole app — same "805
        // too many requests" endpoint seen everywhere else) intermittently comes back empty for
        // just the %-from-Low/%-from-High part even when delta itself resolved fine this tick.
        // Rather than flash those cells to "--" every time that happens, keep the last known-good
        // value for a field until a fresh one actually arrives — but only while the strike itself
        // hasn't changed (a different strike's old %/delta would be meaningless here).
        const mergeSide = (freshSide: typeof fresh.ce, prevSide: typeof fresh.ce | undefined) => {
          if (!prevSide || prevSide.strike !== freshSide.strike) return freshSide;
          return {
            strike: freshSide.strike,
            delta: freshSide.delta ?? prevSide.delta,
            ltp: freshSide.ltp ?? prevSide.ltp,
            pctFromLow: freshSide.pctFromLow ?? prevSide.pctFromLow,
            pctFromHigh: freshSide.pctFromHigh ?? prevSide.pctFromHigh,
          };
        };
        setData(prev => ({
          atmStrike: fresh.atmStrike,
          ce: mergeSide(fresh.ce, prev?.ce),
          pe: mergeSide(fresh.pe, prev?.pe),
        }));
        // getAtmDelta's response already carries the live spot it used to compute the ATM strike
        // — reused here instead of a second poll/endpoint just to show it, per the client's ask
        // for a live spot box where the Delta grid used to sit in each market panel.
        if (fresh.spot) onSpotUpdate?.(fresh.spot);
      }).catch(() => {});
    };
    // Confirmed live as a REAL perf bug (client-reported, directly traced via pm2 logs): this
    // polled Dhan's heavily rate-limited /v2/optionchain Greeks endpoint every 5s — on its own
    // that's under Dhan's own ~1-req/3s community-reported ceiling, but combined with everything
    // else this app polls on the same shared Dhan account (this page's other widgets, other pages
    // left open in other tabs), the account was staying continuously rate-limited, so even the 3
    // backoff retries a real NIFTY Buy click's own strike-LTP scan gets couldn't find a clear
    // window to succeed in. A Delta/Greeks DISPLAY widget doesn't need sub-10s freshness the way
    // real order placement needs rate-limit headroom — widened to 15s to leave meaningfully more
    // room. (SENSEX no longer mounts this component at all — see SpotOnlyPoller above — so the
    // cross-market offset this used to need is gone too.)
    let interval: ReturnType<typeof setInterval> | undefined;
    const startDelay = setTimeout(() => {
      poll();
      interval = setInterval(poll, 15000);
    }, 0);
    return () => { clearTimeout(startDelay); if (interval) clearInterval(interval); };
  }, [expiryDate, market]);

  const cell = (value: number | null, suffix = '') => (
    <div style={{ padding: '3px 5px', fontSize: '9px', fontWeight: 600, borderRadius: '4px', textAlign: 'center', border: '1px solid #d1d5db', background: '#f8f9fa', color: '#212529' }}>
      {value !== null ? `${value.toFixed(2)}${suffix}` : '--'}
    </div>
  );
  // Always rendered (even with no expiry picked yet, showing "--" placeholders) — same
  // always-visible pattern as IVH/NL-NH/N%L-BNL-ITL in this row, so the widget doesn't just
  // silently vanish and look like it was never built.
  return (
    <div>
      <div style={{ fontSize: '8px', fontWeight: 700, color: '#374151', marginBottom: '3px', textAlign: 'center' }}>
        Δ ≥0.8
      </div>
      {/* Client's explicit ask: CALL's own label+boxes sit in ONE row, PUT's own label+boxes in
          another row below it — transposed from the earlier side-by-side-columns layout. */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
          <div style={{ fontSize: '7px', color: '#065f46', minWidth: '20px', textAlign: 'left' }}>CALL</div>
          <div style={{ width: '45px' }}>{cell(data?.ce.pctFromLow ?? null, '%')}</div>
          <div style={{ width: '45px' }}>{cell(data?.ce.pctFromHigh ?? null, '%')}</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
          <div style={{ fontSize: '7px', color: '#991b1b', minWidth: '20px', textAlign: 'left' }}>PUT</div>
          <div style={{ width: '45px' }}>{cell(data?.pe.pctFromLow ?? null, '%')}</div>
          <div style={{ width: '45px' }}>{cell(data?.pe.pctFromHigh ?? null, '%')}</div>
        </div>
      </div>
    </div>
  );
};

// Confirmed live as a REAL perf bug (client report: NIFTY order placement failing with "No
// strikes found" — every scanned strike's LTP came back 0, even after 3 retries): the SENSEX
// panel's Delta ≥0.8 grid is hidden from view (client's own ask), but was STILL running the full
// AtmDeltaBox underneath purely to feed its live-spot box — Dhan's heavily rate-limited
// /v2/optionchain Greeks endpoint plus a quote-details call, every 5s, forever. pm2 logs showed
// constant "805 Too many requests" from exactly this hidden poll at the same moment a real NIFTY
// Buy click's own strike-LTP scan came back empty — this hidden widget was eating into the same
// shared Dhan quote budget the actual order placement needs. Renders nothing; just feeds
// onSpotUpdate from the much cheaper /nifty-buying/spot endpoint (reuses the already-maintained
// live-feed cache, zero extra Dhan API calls) instead.
const SpotOnlyPoller: React.FC<{ market: BuyingMarket; onSpotUpdate: (spot: number) => void }> = ({ market, onSpotUpdate }) => {
  useEffect(() => {
    const poll = () => {
      niftyBuyingAPI.getSpot(market).then(res => {
        if (res.data.spot) onSpotUpdate(res.data.spot);
      }).catch(() => {});
    };
    poll();
    const interval = setInterval(poll, 5000);
    return () => clearInterval(interval);
  }, [market, onSpotUpdate]);
  return null;
};

// Client's hand-drawn formula: Buy/Sell box = favorable spot-points move since entry (Entry spot
// vs current LTP spot), e.g. Entry 24000 -> LTP 24010 = +10 (green, favorable), Entry 24000 ->
// LTP 23870 = -130 (red, unfavorable) — NOT the %-premium ROI computeCombinedRoi below. Each
// position already carries this exact value server-side as `spotDiff` (direction-aware: BULLISH =
// currentSpot - entry_spot, BEARISH = entry_spot - currentSpot) — averaged here across however many
// positions are open on that side.
function computeAvgSpotDiff(positions: NiftyBuyingConfig[]): number | null {
  const diffs = positions.map(p => p.spotDiff).filter((v): v is number => v != null);
  if (diffs.length === 0) return null;
  return diffs.reduce((a, b) => a + b, 0) / diffs.length;
}

// Client's explicit ask (hand-drawn sketch, "Final one"): a TIMER row in the Buy/Sell box ticking
// since the MOST RECENTLY opened position on that side — confirmed "last", not the oldest —
// across however many positions (possibly several strikes) are currently open on it.
function getLastEntryAt(positions: NiftyBuyingConfig[]): string | null {
  if (positions.length === 0) return null;
  return positions.reduce((latest, p) =>
    new Date(p.created_at).getTime() > new Date(latest).getTime() ? p.created_at : latest,
    positions[0].created_at
  );
}

// Client's explicit ask: each open strike already shows its own ROI inline below it, but there
// was no single combined number across all strikes of a side — weighted by invested amount
// (quantity × entry price), not a plain average of the per-strike %s, so a big-quantity strike
// correctly counts for more than a 1-lot one. Placed next to the Delta ≥0.9 widget per request.
function computeCombinedRoi(positions: NiftyBuyingConfig[]): number | null {
  let invested = 0;
  let current = 0;
  for (const p of positions) {
    if (p.quantity && p.entryPrice && p.currentLtp != null) {
      invested += p.quantity * p.entryPrice;
      current += p.quantity * p.currentLtp;
    }
  }
  return invested > 0 ? ((current - invested) / invested) * 100 : null;
}

const BuyingPanel: React.FC<{
  direction: BuyingDirection; optionType: 'CE' | 'PE'; accent: string; bg: string; market: BuyingMarket;
  expiryDate: string; positions: NiftyBuyingConfig[]; onPlaced: () => void;
}> = ({ direction, optionType, accent, bg, market, expiryDate, positions, onPlaced }) => {
  const [form, setForm] = useState<PanelState>(defaultPanelState());
  const [placing, setPlacing] = useState(false);
  const placingRef = useRef(false);
  const [exitingId, setExitingId] = useState<number | null>(null);
  const [clearConfirmId, setClearConfirmId] = useState<number | null>(null);
  const [exitingAllPanel, setExitingAllPanel] = useState(false);
  const [showExitAllPanelConfirm, setShowExitAllPanelConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [placeSummary, setPlaceSummary] = useState<BuyingResult | null>(null);
  const [brokers, setBrokers] = useState<BrokerStatus[]>([]);
  const [featureToggles, setFeatureToggles] = useState<BrokerFeatureToggle[]>([]);
  const [savedDefault, setSavedDefault] = useState<NiftyBuyingDefault | undefined>(undefined);
  const [defaultSaved, setDefaultSaved] = useState(false);
  // NIFTY Buying and SENSEX Buying are independently toggleable per broker now, not one combined
  // 'NIFTY_BUYING' switch — this panel is already per-market (the `market` prop).
  const buyingFeature = market === 'SENSEX' ? 'SENSEX_BUYING' : 'NIFTY_BUYING';
  const isBrokerEnabledFor = (broker: string) =>
    featureToggles.find(t => t.broker === broker && t.feature === buyingFeature)?.is_enabled ?? (broker === 'DHAN');

  useEffect(() => {
    brokerAPI.list().then(res => setBrokers(res.data.filter(b => b.implemented))).catch(() => {});
    brokerAPI.listFeatures().then(res => setFeatureToggles(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    niftyBuyingDefaultsAPI.getDefaults().then(res => {
      const mine = res.data.find(d => d.direction === direction && (d.market ?? 'NIFTY') === market);
      setSavedDefault(mine);
      setForm(prev => applyDefault(prev, mine));
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [direction, market]);

  // Confirmed live TWICE now: a debounced (800ms) and even a pure onBlur save both still lost
  // data — a client typed a value and hit page refresh WHILE THE FIELD WAS STILL FOCUSED (before
  // blur ever fired, let alone an 800ms timer). Fixed by calling persistDefault directly from
  // every onChange below with the field's own just-typed value computed inline (never a stale
  // `form` closure) — a save request goes out on literally every keystroke, so there is no window
  // where a focused, mid-edit field holds a value the server doesn't already have.
  const persistDefault = (f: PanelState) => {
    if (f.capitalPercentage === '' || f.rangeMin === '' || f.rangeMax === '' || f.slippagePercentage === '') return;
    const payload: NiftyBuyingDefault = {
      market, direction, capital_percentage: f.capitalPercentage, range_min: f.rangeMin, range_max: f.rangeMax,
      slippage_percentage: f.slippagePercentage, delay_seconds: f.delaySeconds === '' ? 3 : f.delaySeconds,
      sl_points: f.slPoints === '' ? null : f.slPoints,
      tsl_np_points: f.tslNpPoints === '' ? null : f.tslNpPoints,
      target_points: f.targetPoints === '' ? null : f.targetPoints,
      sl_xy_offset: f.slXYOffset === '' ? null : f.slXYOffset,
      tsl_xy_points: f.tslXYPoints === '' ? null : f.tslXYPoints,
      broker: f.broker,
    };
    niftyBuyingDefaultsAPI.saveDefault(payload).then(() => {
      setSavedDefault(payload);
      setDefaultSaved(true);
      setTimeout(() => setDefaultSaved(false), 1500);
    }).catch((e: any) => setError(e.response?.data?.error || e.message));
  };

  const set = <K extends keyof PanelState>(field: K, value: PanelState[K]) =>
    setForm(prev => ({ ...prev, [field]: value }));

  const handleLoadDefault = () => {
    if (!savedDefault) {
      setError(`No saved default for ${direction} yet — edit a field to create one.`);
      return;
    }
    setForm(prev => applyDefault(prev, savedDefault));
  };

  const handleClearDefault = () => {
    setForm(defaultPanelState());
    setSavedDefault(undefined);
    niftyBuyingDefaultsAPI.clearDefault(market, direction).catch((e: any) => setError(e.response?.data?.error || e.message));
  };

  const handlePlace = async () => {
    if (placingRef.current) return;
    if (!expiryDate) { setError('Please select an expiry date (above)'); return; }
    if (form.capitalPercentage === '' || form.rangeMin === '' || form.rangeMax === '') {
      setError('% Cap and Range Min/Max are required'); return;
    }
    placingRef.current = true;
    setPlacing(true); setError(null); setPlaceSummary(null);
    try {
      const res = await niftyBuyingAPI.placeOrder({
        market, direction, expiryDate,
        capitalPercentage: form.capitalPercentage, rangeMin: form.rangeMin, rangeMax: form.rangeMax,
        slippagePercentage: form.slippagePercentage === '' ? 3 : form.slippagePercentage,
        delaySeconds: form.delaySeconds === '' ? 3 : form.delaySeconds,
        slPoints: form.slPoints === '' ? undefined : form.slPoints,
        tslNpPoints: form.tslNpPoints === '' ? undefined : form.tslNpPoints,
        targetPoints: form.targetPoints === '' ? undefined : form.targetPoints,
        slXYOffset: form.slXYOffset === '' ? undefined : form.slXYOffset,
        tslXYPoints: form.tslXYPoints === '' ? undefined : form.tslXYPoints,
        broker: form.broker,
      });
      setPlaceSummary(res.data);
      // Client's explicit ask: these fields must keep whatever was set (manually, or loaded from
      // the saved default) after placing — they should only ever change via an explicit edit or
      // "Clear", never auto-reset just because an order went through.
      onPlaced();
    } catch (e: any) {
      setError(e.response?.data?.error || e.message);
    } finally {
      placingRef.current = false;
      setPlacing(false);
    }
  };

  const handleExit = async (id: number) => {
    setExitingId(id);
    try {
      await niftyBuyingAPI.exitPosition(id);
      onPlaced();
    } catch (e: any) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setExitingId(null);
    }
  };

  // Per-panel "Exit All" — just this market+direction's open strikes, leaving the other
  // direction and the other market's open positions untouched (unlike the page-level Exit All).
  const handleExitAllPanel = async () => {
    setShowExitAllPanelConfirm(false);
    setExitingAllPanel(true);
    try {
      await niftyBuyingAPI.exitAllForDirection(market, direction);
      onPlaced();
    } catch (e: any) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setExitingAllPanel(false);
    }
  };

  // "Clear" — for a position already closed manually in the Dhan app directly, where a real Exit
  // click would just keep failing (nothing left on the broker side to sell). Confirmed before
  // acting since it never places a real order, only marks it closed in our own records.
  const handleClearPosition = async (id: number) => {
    setClearConfirmId(null);
    setExitingId(id);
    try {
      await niftyBuyingAPI.clearPosition(id);
      onPlaced();
    } catch (e: any) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setExitingId(null);
    }
  };

  const xyLabel = direction === 'BULLISH' ? 'SL (X)' : 'SL (Y)';
  // TRUE trailing stop on the live X count (Bullish) / Y count (Bearish) — tracks the best count
  // seen since entry and exits once it gives back this many points, same shape as TSL(NP) but
  // keyed off the X/Y breadth signal instead of spot price.
  const tslXyLabel = direction === 'BULLISH' ? 'TSL (X)' : 'TSL (Y)';

  return (
    <div style={{
      // Widens to fit a second column (open positions, beside the form) once there's something
      // to show there — client's explicit ask: show already-open positions in that space instead
      // of leaving it blank / pushing them below where they needed a scroll to reach. With no
      // open positions, this is pixel-identical to the previous single-column 420px panel.
      flex: positions.length > 0 ? '0 1 600px' : '0 1 340px', minWidth: '260px', maxWidth: positions.length > 0 ? '760px' : '340px',
      background: bg, border: `1px solid ${accent}33`, borderRadius: '6px', padding: '10px', boxSizing: 'border-box',
    }}>
    <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
    <div style={{ flex: '1 1 300px', minWidth: '280px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {/* Client's explicit ask: no separate NIFTY/SENSEX badge above the panels anymore —
              the market initial is prefixed directly onto BULLISH/BEARISH here instead. */}
          <span style={{ fontSize: '10px', fontWeight: 700, color: accent }}>{market === 'SENSEX' ? 'S' : 'N'}-{direction}</span>
          <button onClick={handlePlace} disabled={placing}
            style={{ padding: '4px 14px', fontSize: '9px', fontWeight: 700, color: 'white', background: placing ? '#9ca3af' : accent, border: 'none', borderRadius: '4px', cursor: placing ? 'default' : 'pointer' }}>
            {placing ? <Spinner size="sm" animation="border" /> : `Buy ${optionType}`}
          </button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          {defaultSaved && (
            <span style={{ fontSize: '8px', fontWeight: 600, color: '#065f46', display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
              <Check size={11} /> Saved
            </span>
          )}
          {positions.length > 0 && (
            // Deliberate extra marginLeft (beyond the row's own 6px gap) — client's explicit ask:
            // this sits right next to the Buy button and accidentally exiting every open position
            // instead of placing a new order is a costly misclick, so it needs a clearly visible
            // gap, not just the same small spacing as the harmless Default/Clear buttons.
            <button onClick={() => setShowExitAllPanelConfirm(true)} disabled={exitingAllPanel}
              title={`Exit all ${positions.length} open ${direction} position(s) — just this side, not the other direction or market`}
              style={{ marginLeft: '18px', padding: '5px 9px', fontSize: '9px', fontWeight: 700, borderRadius: '6px', cursor: exitingAllPanel ? 'default' : 'pointer', border: 'none', background: exitingAllPanel ? '#9ca3af' : '#dc2626', color: 'white' }}>
              {exitingAllPanel ? 'Exiting...' : `Exit All (${positions.length})`}
            </button>
          )}
          <button onClick={handleLoadDefault} title="Load the saved default into this panel's fields"
            style={{ padding: '5px 9px', fontSize: '9px', fontWeight: 600, borderRadius: '6px', cursor: 'pointer', border: '1px solid #9ca3af', background: 'white', color: '#6b7280' }}>
            Default
          </button>
          <button onClick={handleClearDefault} title="Reset this panel's fields AND erase the saved default"
            style={{ padding: '5px 9px', fontSize: '9px', fontWeight: 600, borderRadius: '6px', cursor: 'pointer', border: '1px solid #fca5a5', background: 'white', color: '#991b1b' }}>
            Clear
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '44px 60px 60px 54px', gap: '6px', marginBottom: '8px' }}>
        <div>
          <label style={labelStyle}>% Cap</label>
          <input type="number" min={0} max={100} step="0.5" style={inputStyle} value={form.capitalPercentage}
            onChange={e => { const v = e.target.value === '' ? '' : Number(e.target.value); set('capitalPercentage', v); persistDefault({ ...form, capitalPercentage: v }); }} />
        </div>
        <div>
          <label style={labelStyle}>R Min (₹)</label>
          <input type="number" min={0} style={inputStyle} value={form.rangeMin}
            onChange={e => { const v = e.target.value === '' ? '' : Number(e.target.value); set('rangeMin', v); persistDefault({ ...form, rangeMin: v }); }} />
        </div>
        <div>
          <label style={labelStyle}>R Max (₹)</label>
          <input type="number" min={0} style={inputStyle} value={form.rangeMax}
            onChange={e => { const v = e.target.value === '' ? '' : Number(e.target.value); set('rangeMax', v); persistDefault({ ...form, rangeMax: v }); }} />
        </div>
        <div>
          <label style={labelStyle}>Tgt (NP)</label>
          <input type="number" min={0} placeholder="Off" style={inputStyle} value={form.targetPoints}
            onChange={e => { const v = e.target.value === '' ? '' : Number(e.target.value); set('targetPoints', v); persistDefault({ ...form, targetPoints: v }); }} />
        </div>
      </div>

      <div style={{ marginBottom: '8px' }}>
        <label style={labelStyle}>Brokers</label>
        <div style={{ fontSize: '9px', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '4px', background: '#f8f9fa', color: '#374151', width: '200px', boxSizing: 'border-box' }}>
          {brokers.filter(b => isBrokerEnabledFor(b.broker)).map(b => b.broker.replace('_', '').slice(0, 3)).join(', ') || 'None enabled — turn one on in Broker Management'}
        </div>
      </div>

      {/* Client's explicit ask (temporary — for now, until they say otherwise): hide the
          Slippage (%) field from this panel. form.slippagePercentage/persistDefault's handling of
          it are left untouched so the saved value round-trips unchanged — only this input is
          commented out. Grid narrowed from 5 to 4 columns to match. Uncomment the block below (and
          widen the grid back to repeat(5, 1fr)) to bring it back. */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 48px)', gap: '6px', marginBottom: '8px' }}>
        <div>
          <label style={labelStyle}>{xyLabel}</label>
          <input type="number" min={0} placeholder="Off" style={inputStyle} value={form.slXYOffset}
            onChange={e => { const v = e.target.value === '' ? '' : Number(e.target.value); set('slXYOffset', v); persistDefault({ ...form, slXYOffset: v }); }} />
        </div>
        <div>
          <label style={labelStyle}>{tslXyLabel}</label>
          <input type="number" min={0} placeholder="Off" style={inputStyle} value={form.tslXYPoints}
            onChange={e => { const v = e.target.value === '' ? '' : Number(e.target.value); set('tslXYPoints', v); persistDefault({ ...form, tslXYPoints: v }); }} />
        </div>
        <div>
          <label style={labelStyle}>SL (NP)</label>
          <input type="number" min={0} placeholder="Off" style={inputStyle} value={form.slPoints}
            onChange={e => { const v = e.target.value === '' ? '' : Number(e.target.value); set('slPoints', v); persistDefault({ ...form, slPoints: v }); }} />
        </div>
        <div>
          <label style={labelStyle}>TSL (NP)</label>
          <input type="number" min={0} placeholder="Off" style={inputStyle} value={form.tslNpPoints}
            onChange={e => { const v = e.target.value === '' ? '' : Number(e.target.value); set('tslNpPoints', v); persistDefault({ ...form, tslNpPoints: v }); }} />
        </div>
        {/*
        <div>
          <label style={labelStyle}>Slippage (%)</label>
          <input type="number" step="0.01" style={inputStyle} value={form.slippagePercentage}
            onChange={e => { const v = e.target.value === '' ? '' : Number(e.target.value); set('slippagePercentage', v); persistDefault({ ...form, slippagePercentage: v }); }} />
        </div>
        */}
      </div>

      {error && <div style={{ fontSize: '8px', color: '#991b1b', marginTop: '6px' }}>{error}</div>}

      {placeSummary && (
        <div style={{ fontSize: '8px', marginTop: '6px', padding: '6px', borderRadius: '4px', background: '#f8f9fa', border: '1px solid #d1d5db' }}>
          {placeSummary.brokerResults.map((br, bi) => (
            <div key={bi} style={{ marginBottom: bi < placeSummary.brokerResults.length - 1 ? '6px' : 0 }}>
              <div style={{ fontWeight: 700, color: '#374151' }}>
                {br.broker.replace('_', ' ')}:{' '}
                {br.error ? (
                  <span style={{ color: '#991b1b', fontWeight: 600 }}>{br.error}</span>
                ) : (
                  <>
                    ₹{br.perStrikeAmount.toFixed(0)} × {br.qualifyingStrikeCount} strike{br.qualifyingStrikeCount !== 1 ? 's' : ''}
                    {' '}(total ₹{br.totalDeployAmount.toFixed(0)}) —{' '}
                    {br.results.filter(r => r.success).length}/{br.results.length} placed
                  </>
                )}
              </div>
              {br.results.map((r, i) => (
                <div key={i} style={{ color: r.success ? '#065f46' : '#991b1b', marginTop: '2px' }}>
                  {r.strike}: {r.success ? `✓ ${r.quantity} qty @ ₹${r.price?.toFixed(2)}` : `✗ ${r.error}`}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

    </div>

    {positions.length > 0 && (
        <div style={{ flex: '1 1 300px', minWidth: '280px', borderLeft: `1px solid ${accent}33`, paddingLeft: '14px' }}>
          {positions.map(p => (
            <div key={p.id} style={{ fontSize: '9px', marginBottom: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                <span>
                  {p.entrySymbol || '—'}{' '}
                  <span style={{ color: p.exit_active ? '#065f46' : '#6b7280', fontWeight: 600 }}>{p.exit_active ? 'OPEN' : 'ENTERING'}</span>
                </span>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <button onClick={() => setClearConfirmId(p.id)} disabled={exitingId === p.id}
                    title="Already closed manually in the Dhan app? Mark it closed here too, without placing a new order."
                    style={{
                      padding: '6px 12px', fontSize: '9px', fontWeight: 600, color: '#6b7280',
                      background: 'white', border: '1px solid #9ca3af', borderRadius: '6px',
                      cursor: exitingId === p.id ? 'default' : 'pointer',
                    }}>
                    Clear
                  </button>
                  <button onClick={() => handleExit(p.id)} disabled={exitingId === p.id}
                    style={{
                      padding: '6px 18px', fontSize: '10px', fontWeight: 700, color: 'white',
                      background: exitingId === p.id ? '#9ca3af' : '#dc2626', border: 'none', borderRadius: '6px',
                      cursor: exitingId === p.id ? 'default' : 'pointer',
                      boxShadow: exitingId === p.id ? 'none' : '0 0 8px rgba(220,38,38,0.6)',
                    }}>
                    {exitingId === p.id ? '...' : 'EXIT'}
                  </button>
                </div>
              </div>
              {clearConfirmId === p.id && (
                <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
                  <div style={{ backgroundColor: 'white', borderRadius: '12px', width: '90%', maxWidth: '420px', padding: '24px' }}>
                    <h3 style={{ fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>Clear this position?</h3>
                    <p style={{ fontSize: '11px', color: '#6c757d', marginBottom: '16px' }}>
                      This does NOT place a real exit order — it only marks {p.entrySymbol || 'this position'} closed in our records.
                      Only use this if it's already been closed manually in the Dhan app.
                    </p>
                    <div className="d-flex gap-2 justify-content-end">
                      <button onClick={() => setClearConfirmId(null)} style={{ padding: '8px 16px', border: '1px solid #d1d5db', borderRadius: '6px', background: 'white', cursor: 'pointer' }}>Cancel</button>
                      <button onClick={() => handleClearPosition(p.id)} style={{ padding: '8px 16px', border: 'none', borderRadius: '6px', background: '#6b7280', color: 'white', cursor: 'pointer' }}>Clear</button>
                    </div>
                  </div>
                </div>
              )}
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <TimerBox startedAt={p.created_at} />
                <RoiBox roiPercent={p.roiPercent} />
                <InfoBox label="ENTRY" value={p.entryPrice != null ? `₹${p.entryPrice.toFixed(2)}` : '--'} />
                <InfoBox label="LTP" value={p.currentLtp != null ? `₹${p.currentLtp.toFixed(2)}` : '--'} />
                {/* Client's explicit ask: show the underlying index's spot LTP at entry and its
                    live spot LTP, alongside the option's own ENTRY/LTP above. */}
                <InfoBox label="SPOT ENTRY" value={p.entry_spot != null ? parseFloat(String(p.entry_spot)).toFixed(2) : '--'} />
                <InfoBox label="SPOT LIVE" value={p.currentSpot != null ? p.currentSpot.toFixed(2) : '--'} />
                <InfoBox label="QTY" value={p.quantity != null ? String(p.quantity) : '--'} />
                <InfoBox label="BROKER" value={p.broker ? p.broker.replace('_', ' ') : '--'} />
                {/* Client reported "TSL doesn't work" — traced to a real UX gap: SL(NP)/TSL(NP)
                    are captured ONCE from the form at the moment this position was bought and
                    never change afterward, but the form itself keeps showing whatever value is
                    currently typed in — so a client editing the form later has no way to see
                    what an ALREADY-OPEN position's actual stored value is. These three fields
                    are already in the API response (getOpenPositions does SELECT * + spreads
                    config), just never rendered. TSL(NP) PEAK in particular is the most useful
                    diagnostic: watching it tick up live is direct proof the trailing logic is
                    actually running for this exact position, not just a formula on paper. */}
                <InfoBox label="SL (NP)" value={p.sl_points != null ? String(p.sl_points) : 'Off'} />
                <InfoBox label="TSL (NP)" value={p.tsl_np_points != null ? String(p.tsl_np_points) : 'Off'} />
                <InfoBox label="TSL (NP) PEAK" value={p.tsl_np_peak != null ? parseFloat(String(p.tsl_np_peak)).toFixed(2) : '--'} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>

      {showExitAllPanelConfirm && (
        // Client's explicit ask: an Exit-All confirm must NOT block the view of the open
        // positions behind it — real money, the client needs to see what's happening in the
        // background (e.g. a position already moving) while deciding. Corner popup, no full-
        // screen backdrop, instead of the previous centered modal + dark overlay.
        <div style={{ position: 'fixed', bottom: '20px', right: '20px', zIndex: 9999 }}>
          <div style={{ backgroundColor: 'white', borderRadius: '12px', width: '320px', padding: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.25)', border: '1px solid #e5e7eb' }}>
            <h3 style={{ fontSize: '13px', fontWeight: 600, color: '#dc2626', marginBottom: '6px' }}>Exit all {direction} positions?</h3>
            <p style={{ fontSize: '11px', color: '#6c757d', marginBottom: '12px' }}>
              This will close all {positions.length} open {market} {direction} position(s) shown above — the other direction and the other market stay untouched.
            </p>
            <div className="d-flex gap-2 justify-content-end">
              <button onClick={() => setShowExitAllPanelConfirm(false)} disabled={exitingAllPanel} style={{ padding: '6px 14px', fontSize: '12px', border: '1px solid #d1d5db', borderRadius: '6px', background: 'white', cursor: exitingAllPanel ? 'default' : 'pointer' }}>Cancel</button>
              <button onClick={handleExitAllPanel} disabled={exitingAllPanel}
                // Client's explicit ask: block a repeat click while the whole batch is still
                // exiting (a few seconds for several legs) — a rapid second click here used to be
                // able to kick off a second exitAllForDirection pass concurrently with the first.
                style={{ padding: '6px 14px', fontSize: '12px', border: 'none', borderRadius: '6px', background: exitingAllPanel ? '#9ca3af' : '#dc2626', color: 'white', cursor: exitingAllPanel ? 'default' : 'pointer' }}>
                {exitingAllPanel ? 'Exiting...' : 'Exit All'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ATM±5 reference grid — disabled per client request ("ipo comment panu"), kept defined (not
// deleted) since it's likely to come back; just not rendered below.
// const AtmGridTable: React.FC = () => { ... } — removed from render, see git history for the
// full component if/when this needs to come back.

const MARKETS: { market: BuyingMarket; label: string; accent: string }[] = [
  { market: 'NIFTY', label: 'NIFTY', accent: '#0d6efd' },
  { market: 'SENSEX', label: 'SENSEX', accent: '#fd7e14' },
];

// Client's explicit ask: no NIFTY/SENSEX toggle switching between two hidden views — BOTH
// markets' Bullish CE / Bearish PE panels visible side by side on one screen at the same time.
// Self-contained per market: its own Expiry Date (NIFTY/SENSEX have different weekly expiry
// days, so these were never shareable), its own BUY/SELL spot-diff readout, its own ATM Delta
// box, and its own pair of BuyingPanels. IVH/NL-NH/N%L-BNL-ITL stay OUTSIDE this component,
// rendered once at the page level — those are NIFTY-index readings (India VIX, NIFTY-I futures)
// that were never actually market-toggle-dependent even before this split.
const MarketSection: React.FC<{
  market: BuyingMarket; expiryDate: string;
  positions: NiftyBuyingConfig[]; onPlaced: () => void; spot: number | null;
}> = ({ market, expiryDate, positions, onPlaced, spot }) => {
  const meta = MARKETS.find(m => m.market === market)!;
  const marketPositions = positions.filter(p => (p.market ?? 'NIFTY') === market);

  return (
    <div style={{ flex: '1 1 400px', minWidth: 0, boxSizing: 'border-box', border: `1px solid ${meta.accent}33`, borderRadius: '8px', padding: '14px' }}>
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
        {DIRECTIONS.map(({ direction, optionType, accent, bg }) => (
          <BuyingPanel
            key={`${market}-${direction}`} direction={direction} optionType={optionType} accent={accent} bg={bg} market={market}
            expiryDate={expiryDate}
            positions={marketPositions.filter(p => p.direction === direction)} onPlaced={onPlaced}
          />
        ))}
      </div>
    </div>
  );
};

const NiftyBuyingPage: React.FC = () => {
  const [positions, setPositions] = useState<NiftyBuyingConfig[]>([]);
  const [expiryDateNifty, setExpiryDateNifty] = useState(() => predictExpiryDate('NIFTY'));
  const [expiryDateSensex, setExpiryDateSensex] = useState(() => predictExpiryDate('SENSEX'));
  // Fed by the shared AtmDeltaBox instances' own poll (getAtmDelta already returns the live spot
  // it used for the ATM strike) — client's ask for a live-spot box where Delta used to sit inside
  // each MarketSection panel, without polling a second endpoint just for this.
  const [niftyLiveSpot, setNiftyLiveSpot] = useState<number | null>(null);
  const [sensexLiveSpot, setSensexLiveSpot] = useState<number | null>(null);
  const [exitingAll, setExitingAll] = useState(false);
  const [showExitAllConfirm, setShowExitAllConfirm] = useState(false);
  const [clearingAll, setClearingAll] = useState(false);
  const [showClearAllConfirm, setShowClearAllConfirm] = useState(false);

  // Client's explicit ask: Buy/Sell (points + ROI) now sits beside the Delta ≥0.8 grid in the
  // shared top row instead of inside each MarketSection — same computation MarketSection used to
  // do itself, just hoisted up here since both markets' boxes now render at the page level.
  const niftyPositions = positions.filter(p => (p.market ?? 'NIFTY') === 'NIFTY');
  const sensexPositions = positions.filter(p => p.market === 'SENSEX');
  const niftyBullishSpotDiff = computeAvgSpotDiff(niftyPositions.filter(p => p.direction === 'BULLISH'));
  const niftyBearishSpotDiff = computeAvgSpotDiff(niftyPositions.filter(p => p.direction === 'BEARISH'));
  const niftyBullishCombinedRoi = computeCombinedRoi(niftyPositions.filter(p => p.direction === 'BULLISH'));
  const niftyBearishCombinedRoi = computeCombinedRoi(niftyPositions.filter(p => p.direction === 'BEARISH'));
  const sensexBullishSpotDiff = computeAvgSpotDiff(sensexPositions.filter(p => p.direction === 'BULLISH'));
  const sensexBearishSpotDiff = computeAvgSpotDiff(sensexPositions.filter(p => p.direction === 'BEARISH'));
  const sensexBullishCombinedRoi = computeCombinedRoi(sensexPositions.filter(p => p.direction === 'BULLISH'));
  const sensexBearishCombinedRoi = computeCombinedRoi(sensexPositions.filter(p => p.direction === 'BEARISH'));
  const niftyBullishLastEntryAt = getLastEntryAt(niftyPositions.filter(p => p.direction === 'BULLISH'));
  const niftyBearishLastEntryAt = getLastEntryAt(niftyPositions.filter(p => p.direction === 'BEARISH'));
  const sensexBullishLastEntryAt = getLastEntryAt(sensexPositions.filter(p => p.direction === 'BULLISH'));
  const sensexBearishLastEntryAt = getLastEntryAt(sensexPositions.filter(p => p.direction === 'BEARISH'));

  const fetchPositions = async () => {
    try {
      const res = await niftyBuyingAPI.getOpenPositions();
      const fresh = res.data.filter(p => p.is_active || p.exit_active);
      // currentLtp/roiPercent come from the same shared, rate-limited Dhan quote call documented
      // everywhere else in this app ("805 Too many requests") — a tick that misses it would
      // otherwise flash a perfectly fine position's ROI/LTP to "--" for a few seconds even though
      // nothing about the position actually changed. Hold the last known-good value per position
      // until a fresh one actually arrives, same pattern already used by AtmDeltaBox's own poll.
      setPositions(prev => fresh.map(p => {
        if (p.currentLtp != null) return p;
        const prior = prev.find(pp => pp.id === p.id);
        if (prior && prior.currentLtp != null) {
          return { ...p, currentLtp: prior.currentLtp, roiPercent: prior.roiPercent };
        }
        return p;
      }));
    } catch (err: any) {
      console.error('[NiftyBuying] Failed to fetch open positions:', err.message);
    }
  };

  const handleExitAll = async () => {
    setShowExitAllConfirm(false);
    setExitingAll(true);
    try {
      await niftyBuyingAPI.exitAll();
      fetchPositions();
    } catch (err: any) {
      console.error('[NiftyBuying] Exit all failed:', err.message);
    } finally {
      setExitingAll(false);
    }
  };

  // Bulk "Clear" — for several positions already closed manually in the Dhan app directly
  // (e.g. a batch of stale positions). Never places a real order, only reconciles our records.
  const handleClearAll = async () => {
    setShowClearAllConfirm(false);
    setClearingAll(true);
    try {
      await niftyBuyingAPI.clearAll();
      fetchPositions();
    } catch (err: any) {
      console.error('[NiftyBuying] Clear all failed:', err.message);
    } finally {
      setClearingAll(false);
    }
  };

  useEffect(() => {
    fetchPositions();
    const interval = setInterval(fetchPositions, 5000);
    return () => clearInterval(interval);
  }, []);

  // Load whatever Expiry Date the user last manually set, per market (client's explicit ask:
  // remember it, don't just re-predict every time) — falls back to the auto-predicted next
  // Tuesday/Thursday only if nothing's been saved yet for that market. Both markets load once on
  // mount now — both are visible at the same time, not just whichever one a toggle had selected.
  useEffect(() => {
    niftyBuyingAPI.getLastExpiry('NIFTY').then(res => {
      const saved = res.data.expiry_date;
      if (saved && !isPastDate(saved)) { setExpiryDateNifty(saved); return; }
      const predicted = predictExpiryDate('NIFTY');
      setExpiryDateNifty(predicted);
      if (saved) niftyBuyingAPI.saveLastExpiry('NIFTY', predicted).catch(() => {}); // correct the stale saved value
    }).catch(() => setExpiryDateNifty(predictExpiryDate('NIFTY')));
    niftyBuyingAPI.getLastExpiry('SENSEX').then(res => {
      const saved = res.data.expiry_date;
      if (saved && !isPastDate(saved)) { setExpiryDateSensex(saved); return; }
      const predicted = predictExpiryDate('SENSEX');
      setExpiryDateSensex(predicted);
      if (saved) niftyBuyingAPI.saveLastExpiry('SENSEX', predicted).catch(() => {}); // correct the stale saved value
    }).catch(() => setExpiryDateSensex(predictExpiryDate('SENSEX')));
  }, []);

  const handleExpiryChangeNifty = (value: string) => {
    setExpiryDateNifty(value);
    if (value) niftyBuyingAPI.saveLastExpiry('NIFTY', value).catch(() => {});
  };
  const handleExpiryChangeSensex = (value: string) => {
    setExpiryDateSensex(value);
    if (value) niftyBuyingAPI.saveLastExpiry('SENSEX', value).catch(() => {});
  };

  return (
    <MainLayout>
      {/* Tablet-only shrink — same approach as TradingAppBar's own tablet fix: a single `zoom`
          rule rather than rewriting every one of this page's many inline fontSize/padding/margin
          declarations individually. */}
      <style>{`@media (max-width: 992px) { .nifty-buying-root { zoom: 0.75; } }`}</style>
      <div className="nifty-buying-root" style={{ padding: '20px', paddingTop: '0px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '4px' }}>
          {/* Client's explicit ask: IVH + N%L/BNL/ITL sit on this same row, next to the Expiry
              Date pickers — NL/NH/N-INTRA and the Delta grid stay in the row below. */}
          <IvhBox />
          <div style={{ width: '1px', alignSelf: 'stretch', background: '#d1d5db' }} />
          <NxlBnlItlGroup />
          <div style={{ width: '1px', alignSelf: 'stretch', background: '#d1d5db' }} />
          {/* Client's explicit ask: move both markets' Expiry Date pickers up here (out of each
              MarketSection's own header) so they sit beside the page title, freeing up width
              inside each panel so NIFTY/SENSEX fit side by side more easily. */}
          <div style={{ marginLeft: '24px', display: 'flex', alignItems: 'center', gap: '16px' }}>
            {/* Client's explicit ask: Expiry Date first, then this market's live spot value right
                after it (order reversed from spot-then-date) — moved up from inside
                MarketSection's Buy/Sell row. */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '10px', fontWeight: 700, color: '#0d6efd' }}>N</span>
              <input type="date" style={{ ...inputStyle, width: '98px' }} value={expiryDateNifty} onChange={e => handleExpiryChangeNifty(e.target.value)} />
            </div>
            <LiveSpotBox market="NIFTY" spot={niftyLiveSpot} />
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '10px', fontWeight: 700, color: '#fd7e14' }}>S</span>
              <input type="date" style={{ ...inputStyle, width: '98px' }} value={expiryDateSensex} onChange={e => handleExpiryChangeSensex(e.target.value)} />
            </div>
            <LiveSpotBox market="SENSEX" spot={sensexLiveSpot} />
            {positions.length > 0 && (
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => setShowClearAllConfirm(true)} disabled={clearingAll}
                  title="Already closed manually in the Dhan app? Mark everything closed here too, without placing new orders."
                  style={{
                    padding: '6px 16px', fontSize: '10px', fontWeight: 600, color: '#6b7280',
                    background: 'white', border: '1px solid #9ca3af', borderRadius: '6px',
                    cursor: clearingAll ? 'default' : 'pointer',
                  }}>
                  {clearingAll ? 'Clearing...' : `Clear All (${positions.length})`}
                </button>
                <button
                  onClick={() => setShowExitAllConfirm(true)} disabled={exitingAll}
                  style={{
                    padding: '6px 16px', fontSize: '10px', fontWeight: 700, color: 'white',
                    background: exitingAll ? '#9ca3af' : '#dc2626', border: 'none', borderRadius: '6px',
                    cursor: exitingAll ? 'default' : 'pointer',
                  }}>
                  {exitingAll ? 'Exiting...' : `Exit All (${positions.length} open — NIFTY+SENSEX)`}
                </button>
              </div>
            )}
          </div>
        </div>

        {showExitAllConfirm && (
          // Same corner-popup treatment as the per-panel Exit All confirm above — client's
          // explicit ask: never block the view of open positions behind an Exit-All confirm.
          <div style={{ position: 'fixed', bottom: '20px', right: '20px', zIndex: 9999 }}>
            <div style={{ backgroundColor: 'white', borderRadius: '12px', width: '340px', padding: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.25)', border: '1px solid #e5e7eb' }}>
              <h3 style={{ fontSize: '14px', fontWeight: 600, color: '#dc2626', marginBottom: '6px' }}>Exit All Open Positions?</h3>
              <p style={{ fontSize: '11px', color: '#6c757d', marginBottom: '12px' }}>
                This will close all {positions.length} open Nifty/Sensex Buying position(s) across both markets.
              </p>
              <div className="d-flex gap-2 justify-content-end">
                <button onClick={() => setShowExitAllConfirm(false)} disabled={exitingAll} style={{ padding: '6px 14px', fontSize: '12px', border: '1px solid #d1d5db', borderRadius: '6px', background: 'white', cursor: exitingAll ? 'default' : 'pointer' }}>Cancel</button>
                <button onClick={handleExitAll} disabled={exitingAll}
                  style={{ padding: '6px 14px', fontSize: '12px', border: 'none', borderRadius: '6px', background: exitingAll ? '#9ca3af' : '#dc2626', color: 'white', cursor: exitingAll ? 'default' : 'pointer' }}>
                  {exitingAll ? 'Exiting...' : 'Exit All'}
                </button>
              </div>
            </div>
          </div>
        )}

        {showClearAllConfirm && (
          <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
            <div style={{ backgroundColor: 'white', borderRadius: '12px', width: '90%', maxWidth: '440px', padding: '24px' }}>
              <h3 style={{ fontSize: '15px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>Clear All Positions?</h3>
              <p style={{ fontSize: '12px', color: '#6c757d', marginBottom: '16px' }}>
                This does NOT place real exit orders — it only marks all {positions.length} position(s) closed in our records.
                Only use this if they're already closed manually in the Dhan app.
              </p>
              <div className="d-flex gap-2 justify-content-end">
                <button onClick={() => setShowClearAllConfirm(false)} style={{ padding: '8px 16px', border: '1px solid #d1d5db', borderRadius: '6px', background: 'white', cursor: 'pointer' }}>Cancel</button>
                <button onClick={handleClearAll} style={{ padding: '8px 16px', border: 'none', borderRadius: '6px', background: '#6b7280', color: 'white', cursor: 'pointer' }}>Clear All</button>
              </div>
            </div>
          </div>
        )}
        {/* NL-NH-N-INTRA — client's explicit ask: this is the SAME NIFTY-index reading (NIFTY-I
            futures) for both markets, so shown ONCE here rather than duplicated inside each
            MarketSection (which only reverted a change made moments ago). IVH + N%L/BNL/ITL
            moved up to the Expiry Date row instead. */}
        <div style={{
          display: 'flex', gap: '4px', alignItems: 'flex-start', marginBottom: '14px', flexWrap: 'wrap',
          background: '#f8f9fa', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '4px 12px',
        }}>
          <NlNhGroup />
          {/* Client's explicit ask: move both markets' Delta ≥0.8 grids out of their own
              MarketSection panels and up here, right of N-INTRA, side by side. */}
          <div style={{ width: '1px', alignSelf: 'stretch', background: '#d1d5db' }} />
          <AtmDeltaBox expiryDate={expiryDateNifty} market="NIFTY" onSpotUpdate={setNiftyLiveSpot} />
          {/* Client's explicit ask: Buy/Sell (points + ROI) now sits right of each market's Delta
              grid — moved up from inside MarketSection. */}
          <div style={{ width: '1px', alignSelf: 'stretch', background: '#d1d5db' }} />
          <RoiLabelBox label="Buy" points={niftyBullishSpotDiff} roi={niftyBullishCombinedRoi} lastEntryAt={niftyBullishLastEntryAt} />
          <RoiLabelBox label="Sell" points={niftyBearishSpotDiff} roi={niftyBearishCombinedRoi} lastEntryAt={niftyBearishLastEntryAt} />
          {/* SENSEX Delta ≥0.8 hidden for now, per client's explicit ask ("ipothaiku") — the live
              spot box still needs feeding, but now via SpotOnlyPoller's much cheaper endpoint
              instead of the full (and heavily Dhan-rate-limited) AtmDeltaBox underneath; bring
              AtmDeltaBox back directly (remove SpotOnlyPoller) when the grid itself is unhidden. */}
          <SpotOnlyPoller market="SENSEX" onSpotUpdate={setSensexLiveSpot} />
          <div style={{ width: '1px', alignSelf: 'stretch', background: '#d1d5db' }} />
          <RoiLabelBox label="Buy" points={sensexBullishSpotDiff} roi={sensexBullishCombinedRoi} lastEntryAt={sensexBullishLastEntryAt} secondaryPoints={niftyBullishSpotDiff} secondaryLabel="N" />
          <RoiLabelBox label="Sell" points={sensexBearishSpotDiff} roi={sensexBearishCombinedRoi} lastEntryAt={sensexBearishLastEntryAt} secondaryPoints={niftyBearishSpotDiff} secondaryLabel="N" />
        </div>

        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <MarketSection
            market="NIFTY" expiryDate={expiryDateNifty}
            positions={positions} onPlaced={fetchPositions} spot={niftyLiveSpot}
          />
          <MarketSection
            market="SENSEX" expiryDate={expiryDateSensex}
            positions={positions} onPlaced={fetchPositions} spot={sensexLiveSpot}
          />
        </div>
      </div>
    </MainLayout>
  );
};

export default NiftyBuyingPage;
